// src/components/proposals/proposalBoard.js
//
// The proposals kanban's rules — which column a proposal sits in, and which
// chips its card carries. React-free, and it imports ONLY modules that are
// themselves import-free (proposalStatus, docTypes, onboardingRecord,
// contractModel): importing ProposalsTab pulls ContractsPanel → apiClient →
// MSAL's PublicClientApplication at module load, and a unit test that reaches
// it fails before a single assertion runs. ⚠ The sibling component is
// ProposalKanban.jsx, NOT ProposalBoard.jsx — on a case-insensitive filesystem
// "./ProposalBoard" resolves to this .js file first and the build dies on a
// missing default export (the quote board hit exactly this).
//
// Craig's decisions, 2026-10-02: five columns, contract sent and signed as ONE
// column, the three internal-approval statuses in Drafts with the status chip
// on the card, and PROPOSALS ONLY — leads stay in the list.
//
// ⛔ THE COLUMN IS DERIVED FROM THREE SOURCES AND NEVER STORED:
//     Drafts · Sent · Won            ← proposal.status
//     Contract Sent & Signed         ← contractsFor(contractInstances, id)
//     Contracts & Projects In Autotask ← onboardingRecordOf(proposal, instances)
// FURTHEST WINS. A proposal with an Autotask contract sits in the last column
// whatever its status says; a signed contract puts a proposal still marked
// "sent" in column 4. That last case is a FEATURE: the bookkeeping slipped and
// the board surfaces it. The board READS; it does not reconcile — proposals
// already reconcile to won once, with a stamp (webViewBadges.needsWonReconciliation),
// and the reason is recorded there.
//
// NO DRAG on this board, and the reason is structural: no column is a
// reversible flag. Draft → Sent has a real client send behind it; Won is a real
// client acceptance; Contract and Autotask are derived from OTHER RECORDS — you
// cannot drag a proposal into "has a signed contract" without lying. Do not add
// drag by analogy with the quote board.
import { STATUS_STYLE } from "./proposalStatus";
import { isLead } from "./docTypes";
import { contractsFor, onboardingRecordOf } from "../onboarding/onboardingRecord";
import { CONTRACT_STATUS_META } from "../contracts/contractModel";

export const BOARD_COLUMNS = ["drafts", "sent", "won", "contract", "autotask"];

export const COLUMN_LABELS = {
  drafts: "Drafts",
  sent: "Sent",
  won: "Won",
  contract: "Contract Sent & Signed",
  autotask: "Contracts & Projects In Autotask",
};

/* Statuses in Drafts: draft plus the three ADVISORY internal-approval states
   (proposalStatus.js: they never gate anything). The chip tells them apart. */
export const DRAFT_STATUSES = ["draft", "waiting_approval", "approved", "changes_needed"];

/* A contract instance ADVANCES the proposal to the contract column only once it
   has left the building. Step-0 decision: a `draft` instance (created, never
   sent) does not count; `declined` and `voided` do not count either — a voided
   contract must not park a proposal in the contract column forever. All three
   still get their chip (contractChipFor), because that is the card worth
   spotting. */
export const CONTRACT_ADVANCING = ["sent", "delivered", "signed"];

/* Off the board: lost and archived (reachable through the list's status
   filter), and every lead. */
export const OFF_BOARD_STATUSES = ["lost", "archived"];

/** Has the pipeline written anything to Autotask for this proposal? */
export function autotaskStateFor(proposal, contractInstances = []) {
  const rec = onboardingRecordOf(proposal, contractInstances);
  if (!rec) return null;
  const tickets = Array.isArray(rec.ticketIds) ? rec.ticketIds.length : 0;
  const contract = !!rec.autotaskContractId;
  if (!contract && !tickets) return null;
  return { contract, tickets, onboardingTicket: !!rec.onboardingTicketId };
}

/**
 * Which board column a proposal belongs in. null = not on the board.
 * Never throws; an unrecognised status degrades to Drafts, never to nothing —
 * proposalStatus.js warns that an unrecognised value already drops a record
 * out of the list's equality-matched filter, and the board must not repeat
 * that disappearance.
 */
export function columnFor(proposal, contractInstances = []) {
  if (!proposal || isLead(proposal)) return null;
  const status = proposal.status;
  if (OFF_BOARD_STATUSES.includes(status)) return null;
  if (autotaskStateFor(proposal, contractInstances)) return "autotask";
  const contracts = contractsFor(contractInstances, proposal.id);
  if (contracts.some((c) => CONTRACT_ADVANCING.includes(c.status))) return "contract";
  if (status === "won") return "won";
  if (status === "sent") return "sent";
  return "drafts";
}

export function groupByColumn(proposals, contractInstances = []) {
  const cols = Object.fromEntries(BOARD_COLUMNS.map((c) => [c, []]));
  (proposals || []).forEach((p) => {
    const col = columnFor(p, contractInstances);
    if (col) cols[col].push(p);
  });
  return cols;
}

/* ── Chips — the EXISTING palettes, never new ones ────────────────────────── */

/** The status chip, from STATUS_STYLE; an unknown status reads as Draft. */
export const statusChipFor = (proposal) => STATUS_STYLE[proposal?.status] || STATUS_STYLE.draft;

/* When a proposal has several contract instances, the chip shows the one that
   matters most to a reader scanning the board: a signature outranks a send,
   and a declined or voided envelope outranks an unsent draft because it is
   the one worth spotting. */
const CONTRACT_CHIP_ORDER = ["signed", "delivered", "sent", "declined", "voided", "draft"];

/** The contract chip from CONTRACT_STATUS_META, or null when there is no instance. */
export function contractChipFor(proposal, contractInstances = []) {
  const contracts = contractsFor(contractInstances, proposal?.id);
  if (!contracts.length) return null;
  const status = CONTRACT_CHIP_ORDER.find((s) => contracts.some((c) => c.status === s))
    || contracts[0].status;
  const meta = CONTRACT_STATUS_META[status] || { label: String(status || "Contract"), bg: "#f2f2f2", fg: "#777" };
  return { status, label: `Contract ${meta.label.toLowerCase()}`, bg: meta.bg, fg: meta.fg, count: contracts.length };
}

/** The Autotask chip for the last column: contract ✓ and the ticket count. */
export function autotaskChipFor(proposal, contractInstances = []) {
  const s = autotaskStateFor(proposal, contractInstances);
  if (!s) return null;
  const parts = [];
  if (s.contract) parts.push("Contract ✓");
  const tickets = s.tickets + (s.onboardingTicket ? 1 : 0);
  if (tickets) parts.push(`${tickets} ticket${tickets === 1 ? "" : "s"}`);
  /* The won palette, reused: a record in Autotask is the deal landed. No new colour. */
  return { ...s, label: parts.join(" · "), bg: STATUS_STYLE.won.bg, fg: STATUS_STYLE.won.fg };
}
